/* eslint-disable no-magic-numbers */
import { useContext, useEffect, useState } from 'react'
import { Box, Checkbox, Container, Divider, Grid, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import moment, { Moment } from 'moment'

import PaymentSummarySkeleton from '../../Membership/PaymentSummary/PaymentSummary.skeleton'
import CommonBackButton from '../../../atoms/CommonBackButton/CommonBackButton'
import { Route } from '../../../../router'
import { UserContext } from '../../../../stores/User/UserStore'
import { DatePatterns } from '../../../../types/date-patterns'
import { StripePaymentModal } from '../../../molecules/StripeModal/StripePaymentModal'
import ConfirmationDialog from '../../../molecules/ConfirmationDialog/ConfirmationDialog'
import TermsAndConditionsCheckbox from '../../../organisms/TermsAndConditionsCheckbox/TermsAndConditionsCheckbox'
import { logLandingPageAction } from '../../../../clients/logger'
import { errorPopupMessageTemplate, generateErrorCode, subscriptionGiftPaymentSetupDOBErrorTemplate, subscriptionGiftPaymentSetupMessageTemplate } from '../../../../helpers/logging/templates'
import useTrackPageView from "../../../../helpers/hooks/use-track-page-view"

import styles from './SubscriptionGiftRedemptionPaymentSetup.module.css'
import { useMembershipSettingsRepository } from '../../../../repositories/MembershipSettings/use-membership-settings-repository'
import { useSubscriptionGiftRepository } from '../../../../repositories/SubscriptionGift/use-subscription-gift-repository'
import { useMembershipRepository } from '../../../../repositories/Membership/use-membership-repository'
import { isValidMemberAge, maxMemberAge } from '../../../../helpers/validation/age'
import { setupStripeIntent } from '../../../../clients/subscription-gift'
import LoadingPageContent from '../../../organisms/LoadingPageContent/LoadingPageContent'
import SubscriptionGiftPaymentSummaryDetails from '../../../atoms/SubscriptionGiftPaymentSummaryDetails/SubscriptionGiftPaymentSummaryDetails'
import PaymentSummaryDetailsTotal from '../../../atoms/PaymentSummaryDetailsTotal/PaymentSummaryDetailsTotal'
import { getAgeFromDateOfBirth } from '../../../../helpers/utils/membership'
import LocalStorageKeys from '../../../../types/LocalStorageKeys'

const DECIMALS = 2

const SubscriptionGiftRedemptionPaymentSetup = () => {
    useTrackPageView(`Subscription Gift Redemption Payment Setup Page`)

    const { t } = useTranslation()

    const navigate = useNavigate()
    const { code } = useParams()
    const { retrieveSubscriptionGift, setupStripeSubscriptionFromSubscriptionGift, linkMaxAgeGiftToMembership } = useSubscriptionGiftRepository()

    const { state: userState } = useContext(UserContext)

    const [formStarterTermsConsent, setFormStarterTermsConsent] = useState(false)
    const [dialogErrorMessageTitle, setDialogErrorMessageTitle] = useState(t(`sign_up_failure`))
    const [dialogErrorMessage, setDialogErrorMessage] = useState(``)
    const [subscriptionGiftAcknowledgement, setSubscriptionGiftAcknowledgement] = useState(false)
    const { retrieveMembership } = useMembershipRepository()
    const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false)
    const [isDOBErrorDialogOpen, setIsDOBErrorDialogOpen] = useState(false)
    const [hasRequestedPaymentOrder, setHasRequestedPaymentOrder] = useState(false)
    const { retrieveMembershipSettings } = useMembershipSettingsRepository()
    const [subscriptionPeriod, setSubscriptionPeriod] = useState(0)
    const [subscriptionPurchaseDate, setSubscriptionPurchaseDate] = useState(new Date())
    const [clientSecret, setClientSecret] = useState(``)

    const userEmail = userState.membership?.data?.emailAddress

    useEffect(() => {
        logLandingPageAction(errorPopupMessageTemplate(dialogErrorMessage + `Subscription Gift Redemption Setup page with senderEmail:` + userEmail), `myInfo`)
    }, [dialogErrorMessage])

    useEffect(() => {
        if (!code) {
            navigate(Route.SUBSCRIPTION_GIFT_LANDING)
        }
        if (!userState.membershipSettings?.data){
            retrieveMembershipSettings()
        }
        retrieveSubscriptionGift(code ?? ``).then(subscriptionGift => {
            setSubscriptionPeriod(subscriptionGift.subscriptionPeriod ?? 0)
            setSubscriptionPurchaseDate(subscriptionGift.purchaseDate ?? new Date())
        })
    }, [])

    useEffect(() => {
        retrieveMembership()
    }, [code])

    useEffect(() => {
        if (subscriptionPeriod > 0){
            const memberDOB = moment(userState.membership?.data?.dateOfBirth) ?? moment()
            const memberDOBYear = memberDOB.year()
            const adjustedDate = memberDOB.clone().year(memberDOBYear - subscriptionPeriod)
            if (!isValidMemberAge(adjustedDate.toDate()) && !userState.user?.data?.liftAgeLimit) {
                if (getAgeFromDateOfBirth(adjustedDate.toDate()) > (maxMemberAge ?? 25)){
                    setDialogErrorMessageTitle(t(`sign_up_failure`))
                    setDialogErrorMessage(`Invalid Date of birth. Please contact us at youngntuc@ntuc.org.sg for further assistance` + ` error code: ${generateErrorCode()}`)
                    logLandingPageAction(subscriptionGiftPaymentSetupDOBErrorTemplate(memberDOB, adjustedDate, userEmail) , `myInfo`)
                    setIsDOBErrorDialogOpen(true)
                }else{
                    linkMaxAgeGiftToMembership(code ?? ``, userState.membership?.data?.exchangeID)
                    navigate(Route.MEMBERSHIP_SIGN_UP)
                }
            }
            else if (!hasRequestedPaymentOrder && code) {
                setupStripeIntent(userState.membership?.data?.exchangeID ?? ``).then(stripeClientSecret => {
                    setClientSecret(stripeClientSecret ?? ``)
                })
                setHasRequestedPaymentOrder(true)
            }
        }
    }, [userState.membership, subscriptionPeriod])

    const handleOnSuccess = () => {
        setupStripeSubscriptionFromSubscriptionGift(code ?? ``, userState.membership?.data?.exchangeID).then(() => {
            logLandingPageAction(subscriptionGiftPaymentSetupMessageTemplate(userEmail), `myInfo`)
            localStorage.removeItem(LocalStorageKeys.SUBSCRIPTION_GIFT_REDEMPTION_CODE)
            navigate(Route.MEMBERSHIP_SIGN_UP)
        })
    }

    const showConsentDialog = () => {
        setDialogErrorMessageTitle(t(`sign_up_failure_terms_and_conditions`))
        setDialogErrorMessage(t(`sign_up_terms_and_conditions`) + ` error code: ${generateErrorCode()}`)
        setIsErrorDialogOpen(true)
    }

    const paymentAmountFormatter = (): string => {
        const paymentAmount = 0
        return paymentAmount.toFixed(DECIMALS)
    }

    const membershipExpiryDate = (dateOfBirth: Date, membershipStartDate: Moment): Date => {
        const startDate = membershipStartDate ?? moment()
        const dateOfFutureAge = calculateFutureDateOfMaxAge(dateOfBirth, maxMemberAge ?? 25)
        const numOfYears = moment(dateOfFutureAge).year() - moment(startDate).year()
        const differenceBetweenDates = moment(dateOfFutureAge).diff(moment(startDate), `days`)

        if (differenceBetweenDates < 1) {
            if (userState.user?.data?.liftAgeLimit){
                return moment(startDate).add(1, `years`).subtract(1, `month`).endOf(`month`).set({ hour: 23, minute: 59, second: 59 }).toDate()
            }
            else{
                logLandingPageAction(`membership with email ${userEmail} has 0 number of years left to pay with subscription gift code ${code}`, `myInfo`)
                linkMaxAgeGiftToMembership(code ?? ``, userState.membership?.data?.exchangeID)
                navigate(Route.MEMBERSHIP_SIGN_UP)
            }
        }

        const expirationDate = moment(startDate).add(numOfYears, `years`).subtract(1, `month`).endOf(`month`)
        let cancelAtDate

        if (moment(dateOfFutureAge).isBefore(expirationDate)) {
            cancelAtDate = moment(startDate).add(numOfYears, `years`).subtract(1, `month`).endOf(`month`).set({ hour: 23, minute: 59, second: 59 })
        } else {
            cancelAtDate = moment(startDate).add(numOfYears + 1, `years`).subtract(1, `month`).endOf(`month`).set({ hour: 23, minute: 59, second: 59 })
        }

        return cancelAtDate.toDate()
    }

    const calculateFutureDateOfMaxAge = (dateOfBirth: Date, maxAge: number): Date => {
        const dobObject = moment(dateOfBirth).toDate()
        return moment(dobObject, DatePatterns.DATE).add(maxAge, `years`).toDate()
    }

    if (!clientSecret) {
        return <PaymentSummarySkeleton />
    }

    const paymentSummaryAmount = paymentAmountFormatter()
    const startDate = moment(subscriptionPurchaseDate).startOf(`month`).format(DatePatterns.DATE)
    const endDate = moment(startDate).clone().add(subscriptionPeriod, `years`).subtract(1, `month`).endOf(`month`).format(DatePatterns.DATE)
    const subscriptionEndDate = moment(membershipExpiryDate(userState.membership?.data?.dateOfBirth ?? new Date(), moment(startDate) )).format(DatePatterns.FORMAL_DATE_MONTH_YEAR)

    if (!code){
        return (<Container>
            <CommonBackButton path={Route.HOME} />
            <Typography variant="h4">There should be a redemption gift code given in a proper link. If you are seeing this please use the Contact Us page in the footer</Typography>
        </Container>
        )
    }

    if (!userState.membership?.data) {
        return (
            <LoadingPageContent title={t(`membership_retrieving_title`)} message={t(`membership_retrieving_message`)} />
        )
    }

    return (
        <Container>
            <CommonBackButton onClick={() => {window.location.assign(`${window.location.origin}`)}} />

            <Grid container className={styles.gridContainer}>

                <Grid className={styles.paymentSummaryHeading}>
                    <Typography variant={`h3`} fontWeight={`800`} mb={1}>{t(`payment_summary_subscription_gift_setup_payment_method`)}</Typography>
                    <Typography variant={`h6`}>{t(`payment_summary_subscription_gift_setup_payment_method_subtitle`)}</Typography>
                </Grid>

                <Grid className={styles.paymentSummaryDetails}>
                    <Box className={styles.paymentSummaryDetailsBox}>
                        <Typography variant={`h4`} mb={2}>{t(`payment_summary_subscription_gift_setup_details`)}</Typography>
                        <SubscriptionGiftPaymentSummaryDetails isSetup={true} startDate={startDate} endDate={endDate} paymentSummaryAmount={paymentSummaryAmount} subscriptionPeriod={subscriptionPeriod}/>

                        <Divider className={styles.paymentSummaryDetailsDivider} />

                        <PaymentSummaryDetailsTotal paymentSummaryAmount={paymentSummaryAmount}/>

                        <Grid container direction={`row`} justifyContent={`space-between`} alignItems={`start`} paddingTop={4} spacing={2}>
                            <Grid item>
                                <Typography variant={`body2`}>{t(`subscription_gift_payment_summary_recurring_payment_please_note`)}</Typography>
                            </Grid>
                            <Grid item>
                                <Typography variant={`body2`}>{t(`subscription_gift_payment_setup_recurring_amount`, { recurringAmount: userState.membershipSettings?.data?.membershipFees})}</Typography>
                            </Grid>
                            <Grid item>
                                <Typography variant={`body2`}>{t(`subscription_gift_payment_setup_automatic_cancellation`, { cancelAt: subscriptionEndDate })}</Typography>
                            </Grid>
                        </Grid>
                    </Box>
                    <Grid container paddingTop={4} direction={`row`}>
                        <TermsAndConditionsCheckbox starterTemsConsent={formStarterTermsConsent} onTermsConsentChange={(starterTermsConsent: boolean) => {setFormStarterTermsConsent(starterTermsConsent)}} />
                    </Grid>
                    <Grid container paddingTop={4} direction={`row`}>
                        <Container className={styles.termsContainer}>
                            <Checkbox checked={subscriptionGiftAcknowledgement} onChange={() => setSubscriptionGiftAcknowledgement(!subscriptionGiftAcknowledgement)} color='functional' />
                            <Typography variant="body1" className={styles.termsText}>
                                {t(`subscription_gift_acknowledgement`)}
                            </Typography>
                        </Container>
                    </Grid>

                    <Grid container paddingTop={4} direction={`row`}>
                        <Typography variant="h6">
                            {t(`payment_summary_setup_payment_method_title`)}
                        </Typography>
                        <Typography variant="body1">
                            Rest assured, you will not be charged during your gifted subscription period. You may{` `}
                            <Link to={Route.CONTACT_US} target="_blank" rel="noopener noreferrer" style={{ color: `black`, textDecoration: `underline` }}>
                                contact us
                            </Link>{` `}
                            to cancel your membership at any time.
                        </Typography>
                    </Grid>

                    <Grid container paddingTop={4} direction={`row`}>
                        <StripePaymentModal onConsentFalse={showConsentDialog} consent={formStarterTermsConsent && subscriptionGiftAcknowledgement} clientSecret={clientSecret} email={userEmail} isFirstYearFree={true} isSubscriptionGiftPurchase={false} isSubscriptionGiftRedemption={true} isBundlePurchase={false} onSuccess={handleOnSuccess} />
                    </Grid>
                </Grid>
            </Grid>

            <ConfirmationDialog
                buttonText={t(`common_button_confirmation`)}
                confirmationTitle={dialogErrorMessageTitle}
                confirmationText={dialogErrorMessage}
                dismissButtonText={t(`common_back_to_home`)}
                defaultOpen={isErrorDialogOpen}
                onConfirm={() => {setIsErrorDialogOpen(false)}}
                onDismiss={() => navigate(Route.ROOT)}
            />
            <ConfirmationDialog
                acknowledgeOnly
                buttonText={t(`common_button_confirmation`)}
                confirmationTitle={dialogErrorMessageTitle}
                confirmationText={dialogErrorMessage}
                dismissButtonText={t(`common_back_to_home`)}
                defaultOpen={isDOBErrorDialogOpen}
                onConfirm={() => navigate(Route.ROOT)}
            />
        </Container>
    )
}

export default SubscriptionGiftRedemptionPaymentSetup
